import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Plus, Loader2, CheckCircle, Quote } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    message: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const res = await fetch(`${API_URL}/api/testimonials`);
      const data = await res.json();
      setTestimonials(data);
    } catch (err) {
      console.error('Failed to load testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setIsSuccess(true);
        setFormData({ name: '', company: '', role: '', message: '', rating: 5 });
        setTimeout(() => {
          setIsSuccess(false);
          setShowForm(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to submit testimonial:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={interactive ? 24 : 16}
            className={`${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onSelect && onSelect(star)}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="testimonials" className="py-24 bg-[#152840]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
            {t('testimonials_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            {t('testimonials_title')}
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t('testimonials_subtitle')}
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={40} className="animate-spin text-[#4A90D9]" />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0B1521] rounded-2xl p-6 border border-white/10 relative"
              >
                <Quote size={32} className="absolute top-4 right-4 text-[#4A90D9]/20" />
                <div className="mb-4">
                  {renderStars(testimonial.rating)}
                </div>
                <p className="text-white/80 mb-6 leading-relaxed">"{testimonial.message}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#4A90D9] flex items-center justify-center text-white font-semibold">
                    {testimonial.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-medium">{testimonial.name}</p>
                    {(testimonial.role || testimonial.company) && (
                      <p className="text-white/50 text-sm">
                        {testimonial.role}{testimonial.role && testimonial.company && ' - '}{testimonial.company}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-white/60 mb-12">
            {t('testimonials_empty')}
          </div>
        )}

        {/* Add Testimonial Button */}
        <div className="text-center">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200"
            data-testid="add-testimonial-btn"
          >
            <Plus size={20} />
            {t('testimonials_add')}
          </button>
        </div>

        {/* Testimonial Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowForm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-[#152840] rounded-2xl p-6 border border-white/10"
              >
                <h3 className="text-xl font-semibold text-white mb-6">{t('testimonials_form_title')}</h3>

                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                    <p className="text-white">{t('testimonials_success')}</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">{t('testimonials_name')} *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        data-testid="testimonial-input-name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/80 text-sm mb-2">{t('testimonials_company')}</label>
                        <input
                          type="text"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg input-dark"
                        />
                      </div>
                      <div>
                        <label className="block text-white/80 text-sm mb-2">{t('testimonials_role')}</label>
                        <input
                          type="text"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg input-dark"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm mb-2">{t('testimonials_rating')}</label>
                      {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
                    </div>

                    <div>
                      <label className="block text-white/80 text-sm mb-2">{t('testimonials_message')} *</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg input-dark resize-none"
                        data-testid="testimonial-textarea"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="flex-1 py-3 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-colors"
                      >
                        {t('close')}
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="testimonial-submit-btn"
                      >
                        {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : null}
                        {t('testimonials_submit')}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TestimonialsSection;
